import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow } from "../SectionHeading";
import { resolveAssetUrl } from "../../lib/api";

// Default lumber + steel slides. Editable via admin -> Hero in the future.
const FALLBACK_SLIDES = [
  "/media/photos/lumber-bundled.jpg",
  "/media/photos/steel-erection.jpg",
];
const FALLBACK_POSTER = "/media/photos/framing-worker.jpg";

const DEFAULTS = {
  eyebrow: "Material Supply Specialists",
  headline_line1: "Lumber. Steel.",
  headline_line2: "Delivered Right.",
  subheadline:
    "CM Lindley supplies builders and general contractors with premium dimensional lumber, structural steel, and project-ready materials - backed by hands-on service and on-time delivery.",
  cta_primary_label: "Request a Quote",
  cta_secondary_label: "Explore Services",
  stats: [
    ["100%", "Job-Site Focused"],
  ],
};

const buildStats = (data) => {
  const pairs = [
    [data.stat1_value, data.stat1_label],
    [data.stat2_value, data.stat2_label],
    [data.stat3_value, data.stat3_label],
    [data.stat4_value, data.stat4_label],
  ];
  const anyProvided = pairs.some(([v, l]) => (v && String(v).trim()) || (l && String(l).trim()));
  if (!anyProvided) return DEFAULTS.stats;
  // Only show stats that have a value or a label - drop the empties.
  return pairs
    .map(([v, l]) => [v ?? "", l ?? ""])
    .filter(([v, l]) => (v && v.trim()) || (l && l.trim()));
};

/**
 * Crossfade slideshow with a slow Ken-Burns pan/zoom on each slide.
 * Keeps the "movement" feel of the original video while alternating
 * between lumber and steel imagery.
 */
const HeroSlideshow = ({ slides, poster }) => {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const reduceMotion = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reduceMotion.current = mq.matches;
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    timerRef.current = setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, 6500);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#0A192F]" data-testid="hero-slideshow">
      {slides.map((src, i) => (
        <div
          key={`${src}-${i}`}
          data-testid={`hero-slide-${i}`}
          aria-hidden={i !== active}
          className="absolute inset-0 transition-opacity duration-[1600ms] ease-out"
          style={{ opacity: i === active ? 1 : 0 }}
        >
          <img
            src={resolveAssetUrl(src)}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            className={`w-full h-full object-cover ${reduceMotion.current ? "" : i % 2 === 0 ? "hero-kenburns-a" : "hero-kenburns-b"}`}
            onError={(e) => {
              if (poster) e.currentTarget.src = resolveAssetUrl(poster);
            }}
          />
        </div>
      ))}
    </div>
  );
};

const HeroCopy = ({ hero }) => (
  <div className="max-w-3xl">
    <Eyebrow light>{hero.eyebrow || DEFAULTS.eyebrow}</Eyebrow>
    <h1 className="mt-5 sm:mt-7 font-display font-black tracking-tighter text-white text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95]">
      <span className="block">{hero.headline_line1 || DEFAULTS.headline_line1}</span>
      <span className="block text-[#D4AF37]">
        {hero.headline_line2 || DEFAULTS.headline_line2}
      </span>
    </h1>
    <p className="mt-5 sm:mt-7 text-white/85 text-base sm:text-lg max-w-xl leading-relaxed">
      {hero.subheadline || DEFAULTS.subheadline}
    </p>
    <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
      <Link
        to="/contact"
        data-testid="home-hero-cta-primary"
        className="inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-[#D4AF37] text-[#0A192F] px-6 py-4 sm:px-8 font-display text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold hover:bg-white transition-colors"
      >
        {hero.cta_primary_label || DEFAULTS.cta_primary_label} <ArrowRight size={16} />
      </Link>
      <Link
        to="/services"
        data-testid="home-hero-cta-secondary"
        className="inline-flex w-full sm:w-auto items-center justify-center gap-3 border border-white/40 text-white px-6 py-4 sm:px-8 font-display text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
      >
        {hero.cta_secondary_label || DEFAULTS.cta_secondary_label}
      </Link>
    </div>
  </div>
);

const HeroStatsBar = ({ stats }) => {
  if (!stats || stats.length === 0) return null;
  const isSingle = stats.length === 1;
  const gridClass = isSingle
    ? "flex justify-center"
    : stats.length === 2
    ? "grid grid-cols-2 gap-x-3 gap-y-3 sm:gap-8"
    : stats.length === 3
    ? "grid grid-cols-3 gap-x-3 gap-y-3 sm:gap-8"
    : "grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-3 sm:gap-8";

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-[#0A192F] border-t border-[#D4AF37]/40" data-testid="hero-stats">
      <div className={`max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-4 sm:py-6 ${gridClass} text-white`}>
        {stats.map(([value, label], i) => {
          const hasValue = value && String(value).trim().length > 0;
          return (
            <div key={`${label}-${i}`} className={`min-w-0 ${isSingle ? "text-center flex items-center gap-4 sm:gap-5" : ""}`}>
              {hasValue && (
                <div className={`font-display font-black text-[#D4AF37] leading-none ${isSingle ? "text-3xl sm:text-4xl lg:text-5xl" : "text-lg sm:text-2xl"}`}>
                  {value}
                </div>
              )}
              <div className={`uppercase text-white/85 leading-tight break-words font-display ${isSingle ? "text-xs sm:text-sm tracking-[0.28em] sm:tracking-[0.35em] font-bold" : `text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.25em] ${hasValue ? "mt-1" : "font-bold text-[#D4AF37]"}`}`}>
                {label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const HeroSection = ({ hero }) => {
  const data = hero || {};

  // Prefer explicit admin-managed slides; fall back to local lumber + steel.
  const adminSlides = [data.slide1_url, data.slide2_url, data.slide3_url].filter(Boolean);
  const slides = adminSlides.length >= 2 ? adminSlides : FALLBACK_SLIDES;
  const poster = data.poster_url || FALLBACK_POSTER;

  return (
    <section
      className="relative min-h-[560px] sm:min-h-[640px] h-[88vh] w-full overflow-hidden"
      data-testid="home-hero"
    >
      <HeroSlideshow slides={slides} poster={poster} />
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative h-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 flex flex-col justify-center pt-20 pb-16 sm:pt-20 sm:pb-20">
        <HeroCopy hero={data} />
      </div>
    </section>
  );
};

export default HeroSection;
